import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { api } from "../lib/api";

export default function UserSearch() {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 50);
    }, [open]);

    useEffect(() => {
        const onClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onClick);
            document.removeEventListener("keydown", onKey);
        };
    }, []);

    useEffect(() => {
        if (!q.trim()) { setResults([]); return; }
        let cancelled = false;
        setLoading(true);
        const t = setTimeout(() => {
            api.get(`/users/search?q=${encodeURIComponent(q.trim())}&limit=8`)
                .then(({ data }) => { if (!cancelled) setResults(data); })
                .catch(() => { if (!cancelled) setResults([]); })
                .finally(() => { if (!cancelled) setLoading(false); });
        }, 250);
        return () => { cancelled = true; clearTimeout(t); };
    }, [q]);

    return (
        <div ref={wrapRef} className="relative">
            <button
                data-testid="user-search-btn"
                onClick={() => setOpen((v) => !v)}
                aria-label="Search users"
                className="text-white/70 hover:text-white p-2"
            >
                <MagnifyingGlass size={18} weight="bold" />
            </button>
            {open && (
                <div data-testid="user-search-popover" className="fixed md:absolute top-14 md:top-full left-1/2 md:left-0 -translate-x-1/2 md:translate-x-0 mt-1 md:mt-2 w-[94vw] md:w-80 bg-wta-surface border border-wta-border rounded-sm shadow-2xl z-[60]">
                    <div className="p-3 border-b border-wta-border flex items-center gap-2">
                        <MagnifyingGlass size={14} className="text-wta-muted" />
                        <input
                            ref={inputRef}
                            data-testid="user-search-input"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search users..."
                            className="flex-1 bg-transparent outline-none text-sm placeholder:text-wta-muted"
                        />
                        {q && (
                            <button data-testid="user-search-clear" onClick={() => setQ("")} aria-label="Clear" className="text-wta-muted hover:text-white">
                                <X size={14} />
                            </button>
                        )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                        {!q.trim() ? (
                            <div className="p-4 text-xs text-wta-muted">Type a name to find creators.</div>
                        ) : loading ? (
                            <div className="p-4 text-xs text-wta-muted">Searching…</div>
                        ) : results.length === 0 ? (
                            <div data-testid="user-search-empty" className="p-4 text-xs text-wta-muted">No users found.</div>
                        ) : (
                            <div className="py-1">
                                {results.map((u) => (
                                    <Link
                                        key={u.id}
                                        to={`/profile/${u.id}`}
                                        onClick={() => { setOpen(false); setQ(""); }}
                                        data-testid={`user-search-result-${u.id}`}
                                        className="flex items-center gap-3 px-3 py-2 hover:bg-white/5 transition"
                                    >
                                        <div className="w-9 h-9 rounded-full bg-black/40 border border-wta-border flex items-center justify-center font-heading overflow-hidden shrink-0">
                                            {u.profile_pic ? <img src={u.profile_pic} alt="" className="w-full h-full object-cover" /> : (u.name?.[0]?.toUpperCase() || "?")}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium truncate">@{u.name}</div>
                                            <div className="text-[10px] text-wta-muted">
                                                {u.followers_count ?? 0} followers
                                                {u.role === "admin" && <span className="ml-2 text-wta-primary">· admin</span>}
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
